import { useState, useRef, useEffect, useMemo } from "react";

// ─── Compute KPIs from live datasets ────────────────────────────────────────
function computeKPIs(datasets) {
  const rfis = datasets?.rfis?.rows || [];
  const subs = datasets?.submittals?.rows || [];
  const cos = datasets?.changeOrders?.rows || [];
  const schedule = datasets?.schedule?.rows || [];
  const contracts = datasets?.contracts?.rows || [];
  const quantities = datasets?.quantities?.rows || [];

  const openRFIs = rfis.filter(r => r.status !== "Closed").length;
  const overdueRFIs = rfis.filter(r => r.status !== "Closed" && (parseInt(r.days_open) || 0) > 14).length;
  const pendSubs = subs.filter(r => ["Pending", "Under Review", "Revise & Resubmit"].includes(r.status)).length;
  const coExp = cos.filter(r => !["Approved", "Executed"].includes(r.status))
    .reduce((s, r) => s + (parseFloat(r.submitted_amt) || 0), 0);
  const coApproved = cos.filter(r => r.status === "Approved").length;
  const coTotal = cos.length;
  const contractValue = contracts.reduce((s, r) => s + (parseFloat(r.value) || 0), 0);

  const today = new Date();
  let actSum = 0, planSum = 0, zeroFloat = 0;
  schedule.forEach(r => {
    const fin = new Date(r.finish), st = new Date(r.start);
    const act = parseFloat(r.pct) || 0;
    const plan = fin < today ? 100 : st > today ? 0 : Math.min(100, Math.round(((today - st) / (fin - st)) * 100));
    actSum += act; planSum += plan;
    if ((parseFloat(r.float) || 0) === 0 && act < 100) zeroFloat++;
  });
  const spi = planSum > 0 ? (actSum / planSum) : 1;

  const fmt = n => n >= 1e6 ? "$" + (n / 1e6).toFixed(1) + "M" : n >= 1e3 ? "$" + (n / 1e3).toFixed(0) + "K" : "$" + n.toFixed(0);

  return [
    { id: "rfi", label: "Open RFIs", val: String(openRFIs), color: openRFIs >= 3 ? "var(--kpi-red)" : "var(--kpi-green)", trend: `${overdueRFIs} overdue >14 days`, trendColor: overdueRFIs > 0 ? "var(--kpi-red)" : "var(--kpi-green)", bar: Math.min(100, openRFIs * 10), barColor: "var(--kpi-red)", size: "sm", visible: true },
    { id: "sub", label: "Pending submittals", val: String(pendSubs), color: pendSubs >= 3 ? "var(--kpi-amber)" : "var(--kpi-green)", trend: `${subs.length} total in log`, trendColor: "var(--text-muted)", bar: subs.length > 0 ? Math.round((pendSubs / subs.length) * 100) : 0, barColor: "var(--kpi-amber)", size: "sm", visible: true },
    { id: "co", label: "CO exposure", val: fmt(coExp), color: coExp > 200000 ? "var(--kpi-red)" : coExp > 50000 ? "var(--kpi-amber)" : "var(--kpi-green)", trend: `${cos.filter(r => !["Approved", "Executed"].includes(r.status)).length} unresolved`, trendColor: coExp > 200000 ? "var(--kpi-red)" : "var(--text-muted)", bar: contractValue > 0 ? Math.round((coExp / contractValue) * 100) : 30, barColor: "var(--kpi-red)", size: "sm", visible: true },
    { id: "spi", label: "Schedule SPI", val: spi.toFixed(2), color: spi < 0.85 ? "var(--kpi-red)" : spi < 0.95 ? "var(--kpi-amber)" : "var(--kpi-green)", trend: spi >= 1 ? "On or ahead of schedule" : "Behind planned progress", trendColor: spi >= 0.95 ? "var(--kpi-green)" : "var(--kpi-amber)", bar: Math.round(spi * 100), barColor: spi >= 0.95 ? "var(--kpi-green)" : "var(--kpi-amber)", size: "sm", visible: true },
    { id: "contract", label: "Contract value", val: fmt(contractValue), color: "var(--text-primary)", trend: `${contracts.length} contracts`, trendColor: "var(--text-muted)", bar: 100, barColor: "var(--accent)", size: "sm", visible: true },
    { id: "zfloat", label: "Zero float activities", val: String(zeroFloat), color: zeroFloat > 5 ? "var(--kpi-red)" : zeroFloat > 2 ? "var(--kpi-amber)" : "var(--kpi-green)", trend: "Critical path items incomplete", trendColor: zeroFloat > 2 ? "var(--kpi-amber)" : "var(--text-muted)", bar: schedule.length > 0 ? Math.round((zeroFloat / schedule.length) * 100) : 0, barColor: "var(--kpi-amber)", size: "sm", visible: true },
    { id: "approval", label: "CO approval rate", val: coTotal > 0 ? Math.round((coApproved / coTotal) * 100) + "%" : "N/A", color: coTotal > 0 && (coApproved / coTotal) >= 0.75 ? "var(--kpi-green)" : "var(--kpi-amber)", trend: `${coApproved} of ${coTotal} approved`, trendColor: "var(--text-muted)", bar: coTotal > 0 ? Math.round((coApproved / coTotal) * 100) : 0, barColor: "var(--kpi-green)", size: "sm", visible: false },
    { id: "activities", label: "Total activities", val: String(schedule.length), color: "var(--text-primary)", trend: `${schedule.filter(r => (parseFloat(r.pct) || 0) >= 100).length} complete`, trendColor: "var(--kpi-green)", bar: schedule.length > 0 ? Math.round((schedule.filter(r => (parseFloat(r.pct) || 0) >= 100).length / schedule.length) * 100) : 0, barColor: "var(--kpi-green)", size: "sm", visible: false },
    { id: "elements", label: "BIM elements", val: String(quantities.length), color: "var(--text-primary)", trend: `${quantities.filter(r => Math.abs(parseFloat(r.variance_pct) || 0) > 3).length} with >3% variance`, trendColor: "var(--kpi-amber)", bar: 100, barColor: "var(--accent)", size: "sm", visible: false },
  ];
}

// ─── ExecDashboard ──────────────────────────────────────────────────────────
export default function ExecDashboard({ datasets, onAsk, messages, loading }) {
  const initialKPIs = useMemo(() => computeKPIs(datasets), [datasets]);
  const [kpis, setKpis] = useState(initialKPIs);
  const [panelOpen, setPanelOpen] = useState(false);
  const [responseOpen, setResponseOpen] = useState(false);
  const dragSrc = useRef(null);
  const panelRef = useRef(null);
  const responseRef = useRef(null);

  useEffect(() => { setKpis(computeKPIs(datasets)); }, [datasets]);

  // Close customize panel on outside click
  useEffect(() => {
    if (!panelOpen) return;
    function handleClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) setPanelOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [panelOpen]);

  // Open response panel when new messages arrive, scroll to bottom
  const chatMessages = (messages || []).filter(m => !m.isWelcome);
  useEffect(() => {
    if (chatMessages.length > 0 || loading) setResponseOpen(true);
    if (responseRef.current) responseRef.current.scrollTop = responseRef.current.scrollHeight;
  }, [chatMessages.length, loading]);

  function toggleVisibility(idx) {
    setKpis(prev => prev.map((k, i) => i === idx ? { ...k, visible: !k.visible } : k));
  }
  function resizeCard(idx, size) {
    setKpis(prev => prev.map((k, i) => i === idx ? { ...k, size } : k));
  }
  function resetLayout() {
    setKpis(prev => prev.map((k, i) => ({ ...k, size: "sm", visible: i < 6 })));
  }
  function handleDragStart(idx) { dragSrc.current = idx; }
  function handleDragEnd() { dragSrc.current = null; }
  function handleDrop(targetIdx) {
    if (dragSrc.current === null || dragSrc.current === targetIdx) return;
    setKpis(prev => {
      const visible = prev.filter(k => k.visible);
      const hidden = prev.filter(k => !k.visible);
      const srcVisIdx = visible.findIndex(k => prev.indexOf(k) === dragSrc.current);
      const tgtVisIdx = visible.findIndex(k => prev.indexOf(k) === targetIdx);
      if (srcVisIdx === -1 || tgtVisIdx === -1) return prev;
      const item = visible.splice(srcVisIdx, 1)[0];
      visible.splice(tgtVisIdx, 0, item);
      return [...visible, ...hidden];
    });
  }

  const [askInput, setAskInput] = useState("");
  function handleAsk() {
    if (!askInput.trim()) return;
    onAsk(askInput.trim());
    setAskInput("");
  }

  return (
    <div className="exec-dashboard">
      {/* Toolbar */}
      <div className="exec-toolbar">
        <div className="exec-toolbar-label">Dashboard</div>
        <div className="exec-toolbar-actions">
          <button className={`exec-toolbar-btn${panelOpen ? " active" : ""}`} onClick={() => setPanelOpen(p => !p)}>
            Customize
          </button>
          <button className="exec-toolbar-btn" onClick={resetLayout}>Reset layout</button>
        </div>
      </div>

      {/* Customize panel — with close button + outside click dismiss */}
      {panelOpen && (
        <div className="exec-toggle-panel" ref={panelRef}>
          <div className="exec-toggle-header">
            <div className="exec-toggle-title">Visible KPIs</div>
            <button className="exec-toggle-close" onClick={() => setPanelOpen(false)}>&times;</button>
          </div>
          {kpis.map((k, i) => (
            <div key={k.id} className="exec-toggle-row" onClick={() => toggleVisibility(i)}>
              <div className={`exec-toggle-check${k.visible ? " on" : ""}`} />
              <span>{k.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* KPI grid */}
      <div className="exec-grid-area">
        <div className="exec-grid">
          {kpis.filter(k => k.visible).map(k => {
            const realIdx = kpis.indexOf(k);
            return (
              <div key={k.id}
                className={`exec-kcard exec-kcard-${k.size}`}
                draggable
                onDragStart={() => handleDragStart(realIdx)}
                onDragEnd={handleDragEnd}
                onDragOver={e => e.preventDefault()}
                onDrop={() => handleDrop(realIdx)}
              >
                <div className="exec-kcard-head">
                  <div className="exec-kcard-label">{k.label}</div>
                  <div className="exec-kcard-actions">
                    <div className="exec-resize-btns">
                      <button className="exec-resize-btn" onClick={() => resizeCard(realIdx, "sm")}>S</button>
                      <button className="exec-resize-btn" onClick={() => resizeCard(realIdx, "md")}>M</button>
                      <button className="exec-resize-btn" onClick={() => resizeCard(realIdx, "lg")}>L</button>
                    </div>
                    <span className="exec-kcard-grip" title="Drag to reorder">::</span>
                  </div>
                </div>
                <div className="exec-kcard-val" style={{ color: k.color }}>{k.val}</div>
                <div className="exec-kcard-trend" style={{ color: k.trendColor }}>{k.trend}</div>
                <div className="exec-kcard-bar">
                  <div className="exec-kcard-fill" style={{ width: `${k.bar}%`, background: k.barColor }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Slide-up response panel — appears when CHAD responds */}
      <div className={`exec-response${responseOpen ? " open" : ""}`}>
        <div className="exec-response-header">
          <span className="exec-response-title">Chad Response</span>
          <button className="exec-response-close" onClick={() => setResponseOpen(false)} title="Minimize">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
        </div>
        <div className="exec-response-body" ref={responseRef}>
          {chatMessages.map((msg, i) => (
            <div key={i} className={`exec-msg${msg.role === "user" ? " user" : ""}`}>
              <div className="exec-msg-text">{msg.text}</div>
              {msg.insight && <div className="exec-msg-insight">{msg.insight}</div>}
              {msg.followUpSuggestions?.length > 0 && (
                <div className="exec-msg-follows">
                  {msg.followUpSuggestions.map((s, j) => (
                    <button key={j} className="exec-msg-follow" onClick={() => onAsk(s)}>{s}</button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {loading && <div className="exec-msg"><span style={{ color: "var(--text-faint)", fontSize: 14 }}>Thinking...</span></div>}
        </div>
      </div>

      {/* Ask bar */}
      <div className="exec-ask-bar">
        <input className="exec-ask-input"
          placeholder="Ask Chad about portfolio performance..."
          value={askInput}
          onChange={e => setAskInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") handleAsk(); }}
        />
        <button className="exec-ask-send" onClick={handleAsk}>Ask</button>
      </div>
    </div>
  );
}
